import last from 'lodash/last';
import max from 'lodash/max';
import type { IAPProduct } from '../iap/types';
import type { PurchasesList } from '../../libs/offerSystem/OfferSystemHistory';
import { preloadLayoutsAssets } from '../../views/preloadLayoutsAssets';
import {
  createLayoutInstanceFromPath,
  createLayoutInstanceFromRaw,
} from '../../libs/layouts/runtime/createLayoutInstance';
import type { SpecialOfferLayout } from '../../libs/layouts/types/LayoutShapes';
import { applyLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';

export function getMostExpensivePurchaseCost(purchaseList: PurchasesList, period: number): number {
  if (purchaseList.length === 0) return 0;

  const recentPurchaseHistory = purchaseList.filter((entry) => entry.date > Date.now() - period);

  return max(recentPurchaseHistory.map((entry) => entry.usdCentPrice)) || 0;
}

export function findPreferredOffer(offers: IAPProduct[], maxUserSpend: number): IAPProduct | null {
  if (offers.length === 0) return null;

  const priceSortedOffers = offers.sort((a, b) => a.usdCentPrice - b.usdCentPrice);
  const preferredOffer = priceSortedOffers.find((product) => product.usdCentPrice > maxUserSpend);

  if (preferredOffer) return preferredOffer;

  const highestPriceOffer = last(priceSortedOffers);
  if (highestPriceOffer) return highestPriceOffer;

  return null;
}

export async function createOfferPopupLayout<T>(product: IAPProduct): Promise<T | null> {
  const layoutData = product.data.popupLayout;
  if (!layoutData) return null;

  const layoutInput = layoutData?.raw ? layoutData.raw : (layoutData?.path ?? null);
  if (layoutInput) {
    const ok = await preloadLayoutsAssets([layoutInput], true);
    if (!ok) return null;
  } else {
    return null;
  }

  let layout: T | null = null;

  if (layoutData?.raw) {
    layout = createLayoutInstanceFromRaw<SpecialOfferLayout>(layoutData.raw) as T;
  } else if (layoutData?.path) {
    layout = createLayoutInstanceFromPath<SpecialOfferLayout>(layoutData.path) as T;
  }

  if (layout && layoutData.skin) {
    applyLayoutState(layout, layoutData.skin);
  }

  return layout;
}
